import type {
  LiveTrackerSound,
  LiveActivityEvent,
  LiveActivityOverview,
  AbandonedCartsFilters,
  AbandonedCartsResponse,
} from 'src/types/live-activity';

import useSWR from 'swr';
import { useMemo } from 'react';

import { liveActivityApi } from 'src/api/live-activity.api';

type Options = {
  /** Milisegundos entre refrescos. 0 = pausado. */
  refreshInterval?: number;
  onlineWindowSeconds?: number;
  eventsMinutes?: number;
  /** false desactiva la petición del todo (no solo el refresco). Por defecto true. */
  enabled?: boolean;
};

/**
 * Sondea el resumen en vivo. Se usa polling (no websockets) porque el back-office ya habla con
 * la Admin API por HTTP y así no hace falta infraestructura nueva en el servidor.
 */
export function useLiveActivity({
  refreshInterval = 4000,
  onlineWindowSeconds = 120,
  eventsMinutes = 15,
  enabled = true,
}: Options = {}) {
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? ['live-activity', onlineWindowSeconds, eventsMinutes] : null,
    () => liveActivityApi.getOverview({ onlineWindowSeconds, eventsMinutes, eventsLimit: 80 }),
    {
      refreshInterval,
      // Mientras se refresca se conserva la foto anterior: así las tarjetas no parpadean.
      keepPreviousData: true,
      revalidateOnFocus: true,
      // Un fallo puntual de red no debe vaciar la pantalla.
      shouldRetryOnError: true,
      dedupingInterval: 1000,
    }
  );

  return useMemo(() => {
    const overview = data?.data as LiveActivityOverview | undefined;

    return {
      overview,
      users: overview?.users ?? [],
      events: overview?.events ?? [],
      stats: overview?.stats,
      serverTimeUtc: overview?.serverTimeUtc,
      isLoading: isLoading && !overview,
      error,
      refresh: mutate,
    };
  }, [data, error, isLoading, mutate]);
}

/**
 * Recorrido completo de un usuario (ficha lateral). Solo se pide mientras la ficha está abierta;
 * `userId` a null desactiva la petición.
 */
export function useLiveActivityTimeline(userId: number | null, refreshInterval = 5000) {
  const { data, error, isLoading } = useSWR(
    userId ? ['live-activity-timeline', userId] : null,
    () => liveActivityApi.getUserTimeline(userId as number),
    { refreshInterval, keepPreviousData: true }
  );

  return useMemo(
    () => ({
      timeline: (data?.data as LiveActivityEvent[] | undefined) ?? [],
      timelineLoading: isLoading,
      timelineError: error,
    }),
    [data, error, isLoading]
  );
}

/** Sonidos configurados para los avisos (pedido y recarga). `enabled=false` no llega a pedirlos. */
export function useLiveTrackerSounds(enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? 'live-tracker-sounds' : null,
    () => liveActivityApi.getSounds()
  );

  return useMemo(() => {
    const sounds = (data?.data as LiveTrackerSound[] | undefined) ?? [];
    return {
      sounds,
      soundByKind: Object.fromEntries(sounds.map((sound) => [sound.kind, sound])) as Record<
        string,
        LiveTrackerSound | undefined
      >,
      soundsLoading: isLoading,
      soundsError: error,
      refreshSounds: mutate,
    };
  }, [data, error, isLoading, mutate]);
}

/**
 * Anunciantes dentro del panel en una ventana más amplia (por defecto 5 minutos), para el
 * listado que se abre al pulsar la métrica "Anunciantes dentro". Solo se pide con el modal
 * abierto (`enabled`).
 */
export function useLiveActivityOnlineUsers(enabled: boolean, windowSeconds = 300) {
  const { data, error, isLoading } = useSWR(
    enabled ? ['live-activity-online', windowSeconds] : null,
    () =>
      liveActivityApi.getOverview({
        onlineWindowSeconds: windowSeconds,
        eventsMinutes: 1,
        eventsLimit: 1,
      }),
    { refreshInterval: 10000, keepPreviousData: true }
  );

  return useMemo(() => {
    const overview = data?.data as LiveActivityOverview | undefined;
    return {
      onlineUsers: overview?.users ?? [],
      onlineLoading: isLoading && !overview,
      onlineError: error,
      onlineServerTimeUtc: overview?.serverTimeUtc,
    };
  }, [data, error, isLoading]);
}

/**
 * Carritos abandonados (histórico de 30 días). Solo se pide con la pestaña abierta; se refresca
 * cada 30 s porque no es una pantalla "en directo" sino de trabajo comercial.
 */
export function useAbandonedCarts(filters: AbandonedCartsFilters, enabled = true) {
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? ['abandoned-carts', filters] : null,
    () => liveActivityApi.getAbandonedCarts(filters),
    { refreshInterval: 30000, keepPreviousData: true }
  );

  return useMemo(() => {
    const response = data?.data as AbandonedCartsResponse | undefined;

    return {
      carts: response?.items ?? [],
      cartsSummary: response,
      cartsLoading: isLoading && !response,
      cartsError: error,
      refreshCarts: mutate,
    };
  }, [data, error, isLoading, mutate]);
}
